
import { MapPin, ShoppingCart, Search, Percent } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { useState, useEffect, useRef } from 'react';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useSearch } from '@/hooks/useSearch';
import SearchResults from '@/components/SearchResults';
import { reverseGeocode } from '@/utils/osmGeocoding';

interface SavedLocation {
  id: string;
  name: string;
  address_line: string;
  city: string;
  is_default: boolean;
  latitude?: number;
  longitude?: number;
}

const TopNavigation = () => {
  const { getRestaurantCartCount } = useCart();
  const { user } = useAuth();
  const [selectedLocation, setSelectedLocation] = useState<string>('Select location');
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const { restaurants, menuItems, loading } = useSearch(searchQuery);

  useEffect(() => {
    loadLocation();

    // Listen for storage changes
    const handleStorageChange = () => {
      loadLocation();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('locationUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('locationUpdated', handleStorageChange);
    };
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadLocation = async () => {
    // First check if user has GPS location from useLocationPermission
    const userLocationStr = localStorage.getItem('userLocation');
    if (userLocationStr) {
      try {
        const userLocation = JSON.parse(userLocationStr);
        // Use reverse geocoding to get address from GPS coordinates
        const address = await reverseGeocode(userLocation.latitude, userLocation.longitude);
        if (address) {
          setSelectedLocation(address.length > 25 ? `${address.substring(0, 25)}...` : address);
          return;
        }
      } catch (error) {
        console.error('Error using GPS location:', error);
      }
    }

    if (user) {
      // Fallback to saved locations if no GPS
      try {
        const { data, error } = await supabase
          .from('saved_locations')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_default', true)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading user location:', error);
          loadFromLocalStorage();
          return;
        }

        if (data) {
          const locationText = formatAddress(data.address_line, data.name);
          setSelectedLocation(locationText);
        } else {
          loadFromLocalStorage();
        }
      } catch (error) {
        console.error('Error loading user location:', error);
        loadFromLocalStorage();
      }
    } else {
      loadFromLocalStorage();
    }
  };

  const loadFromLocalStorage = () => {
    const savedLocationStr = localStorage.getItem('selectedLocation');
    if (savedLocationStr) {
      try {
        const location = JSON.parse(savedLocationStr);
        const locationText = formatAddress(location.address);
        setSelectedLocation(locationText);
      } catch (error) {
        console.error('Error parsing saved location:', error);
        setSelectedLocation('Select location');
      }
    }
  };

  const formatAddress = (address: string, name?: string): string => {
    if (name && ['Home', 'Work'].includes(name)) {
      return name;
    }
    
    return address.length > 25
      ? `${address.substring(0, 25)}...`
      : address;
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowResults(value.length >= 2);
  };

  const handleSearchFocus = () => {
    if (searchQuery.length >= 2) {
      setShowResults(true);
    }
  };

  const handleNavClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="sticky top-0 bg-white border-b border-gray-200 z-40 px-4 py-3" onClick={handleNavClick}>
      <div className="flex items-center justify-between mb-3">
        <Link
          to="/location"
          className="flex items-center gap-2 text-gray-700 hover:bg-gray-50 px-2 py-1 rounded-md transition-colors"
          onClick={handleNavClick}
        >
          <MapPin className="h-5 w-5" />
          <div className="text-left">
            <span className="text-xs block">Deliver to</span>
            <span className="text-sm font-medium block">{selectedLocation}</span>
          </div>
        </Link>
        
        <div className="flex items-center gap-2">
          <Link
            to="/promo"
            className="text-gray-700 hover:bg-gray-50 p-2 rounded-md transition-colors"
            onClick={handleNavClick}
          >
            <Percent className="h-6 w-6" />
          </Link>
          
          <Link
            to="/cart"
            className="text-gray-700 relative hover:bg-gray-50 p-2 rounded-md transition-colors"
            onClick={handleNavClick}
          >
            <ShoppingCart className="h-6 w-6" />
            {getRestaurantCartCount() > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full text-xs w-5 h-5 flex items-center justify-center font-bold">
                {getRestaurantCartCount()}
              </span>
            )}
          </Link>
        </div>
      </div>
      
      <div className="relative" ref={searchRef}>
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <Input
          placeholder="Search for food or restaurants"
          className="pl-10 bg-gray-50 border-gray-200"
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
        />
        {showResults && (
          <SearchResults
            restaurants={restaurants}
            menuItems={menuItems}
            loading={loading}
            onClose={() => setShowResults(false)}
          />
        )}
      </div>
    </div>
  );
};

export default TopNavigation;
